import { HttpClient, HttpParams, } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as _ from 'lodash';
import { CacheService, ClassMapper, CLOSE_PERIOD_TYPES, ClosePeriod, AuthService, ROLES } from "mapfre-auditlib";
import moment from "moment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";

@Injectable({
   providedIn: "root"
})
export class ClosePeriodService {
   constructor(private http: HttpClient, private cacheService: CacheService, private authService: AuthService) { }

   className = 'ClosePeriodService';

   getAllClosePeriods(cache = true, cacheKey = "allClosePeriods"): Observable<ClosePeriod[]>| any {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey);
       } else {
         let observable = this.http.get<ClosePeriod[]>(environment.base_URL + "/close-periods").pipe(
            map(res => {
               res = ClassMapper.fromJSONArray(res, ClosePeriod);
               this.cacheService.updateCacheData(res, cacheKey);
               return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
       }
   }

   getClosePeriodsByExercise(exercise, cache = true, cacheKey = "closePeriodsExercise"): Observable<ClosePeriod[]> | any{
      let params = new HttpParams().set('exercise', exercise);
      if (cache && this.cacheService.canUseCache(cacheKey, [exercise.toString()])) {
         return this.cacheService.getCacheData(cacheKey, [exercise.toString()]);
       } else {
         let observable = this.http.get<ClosePeriod[]>(environment.base_URL + "/close-periods", { params: params }).pipe(
            map(res => {
               res = _.filter(res, it => it.mrkActive);
               this.cacheService.updateCacheData(res, cacheKey, [exercise.toString()])
               return ClassMapper.fromJSONArray(res, ClosePeriod);
            })
         );
         this.cacheService.updateCacheReq(cacheKey, [exercise.toString()]);
         return observable;
      }
   }

   newClosePeriod(newClosePeriod) {
      return this.http.post(environment.base_URL + "/close-periods", newClosePeriod);
   }

   updateClosePeriod(editedClosePeriod, closePeriodId) {
      return this.http
         .put(environment.base_URL + `/close-periods/${closePeriodId}`, editedClosePeriod)
   }

   /**
    * Devuelve si a determinado modelo le aplica cierre en base a la fecha de cierre.
    * Si hay un cierre para el año en cuestion, si la fecha del modelo es anterior a la fecha de cierre no se permitira
    * 
    * @param date 
    * @param closeDate 
    * @returns boolean false si no aplica cierre, true si aplica cierre
    * 
    * @throws Error si el tipo de las fechas es incorrecto
    */
   isBeforeCloseDate(date: moment.MomentInput, closeDate: moment.MomentInput, isAdmin: boolean = false) {
      if (isAdmin) return false;

      const previousDateMoment = moment(date);
      const closeDateMoment = moment(closeDate);

      if (!previousDateMoment.isValid() || !closeDateMoment.isValid()) {
         throw new Error('Fecha inválida proporcionada');
      }

      return previousDateMoment.isSameOrBefore(closeDateMoment)
   }

   isClosed(modelDate: moment.MomentInput, isAdmin: boolean = false, closePeriods: any[], secondaryCloseTypePk?: number): boolean{
      let isClosed = false;
      const principalClosed = closePeriods.find(
         item => item.typeClosePeriod.idTypeClosePeriodPk === CLOSE_PERIOD_TYPES.CIERRE_PRINCIPAL && item.mrkActive
      );
      const secondaryClose = closePeriods.find(
         item => item.typeClosePeriod.idTypeClosePeriodPk === secondaryCloseTypePk && item.mrkActive
      );

      if (principalClosed) {
         isClosed = this.isBeforeCloseDate(modelDate, principalClosed.dateFinish, isAdmin)
         if (isClosed) return isClosed; 
      }
      if (secondaryClose) {
         isClosed = this.isBeforeCloseDate(modelDate, secondaryClose.dateFinish, isAdmin)
         if (isClosed) return isClosed; 
      }
      return isClosed;
   }

   isClosedAsync(modelDate: number, isAdmin: boolean = false, secondaryCloseTypePk?: number): Observable<boolean> {
      return this.getClosePeriodsByExercise(modelDate, true, "closePeriodsExercise").pipe(
         map((closePeriods: any[]) => {
          console.log(closePeriods);
            let isClosed = false;
            const principalClosed = closePeriods.find(
               item => item.typeClosePeriod.idTypeClosePeriodPk === CLOSE_PERIOD_TYPES.CIERRE_PRINCIPAL && item.mrkActive
            );
            const secondaryClose = closePeriods.find(
               item => item.typeClosePeriod.idTypeClosePeriodPk === secondaryCloseTypePk && item.mrkActive
            );

            if (principalClosed) {
               isClosed = this.isBeforeCloseDate(modelDate, principalClosed.dateFinish, isAdmin);
               if (isClosed) return isClosed;
            }
            if (secondaryClose) {
               isClosed = this.isBeforeCloseDate(modelDate, secondaryClose.dateFinish, isAdmin);
               if (isClosed) return isClosed;
            }
            return isClosed;
         })
      );
   }

   getCloseDate(closePeriods: any[], secondaryCloseTypePk?: number): moment.MomentInput{
      const principalClose = closePeriods.find(
         item => item.typeClosePeriod.idTypeClosePeriodPk === CLOSE_PERIOD_TYPES.CIERRE_PRINCIPAL && item.mrkActive
      );
      const secondaryClose = closePeriods.find(
         item => item.typeClosePeriod.idTypeClosePeriodPk === secondaryCloseTypePk && item.mrkActive
      );

      if (!principalClose && !secondaryClose) {
         return null
      }

      if (!principalClose ){
         return secondaryClose.dateFinish
      } 

      if (!secondaryClose) {
         return principalClose.dateFinish
      }
         
      return principalClose.dateFinish.isAfter(secondaryClose.dateFinish) ? principalClose.dateFinish : secondaryClose.dateFinish;
   }

   hasEditingPermission(): boolean {
     return this.authService.userHasAtleastOneRole([ROLES.ADMINISTRADOR, ROLES.DIRECTOR, ROLES.SUBDIRECTOR])
   }

}
